import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, MapPin, Crosshair, Search, X } from "lucide-react";

const emptyForm = { name: "", address: "", city: "", state: "", latitude: 0, longitude: 0, radius_meters: 150, active: true, client_billable_rate: 0, is_remote: false, wc_class_code: "", wc_rate_per_100: 0 };
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Build the URL for Mapbox's static-map preview (no library required —
// just an <img>). Circle overlay approximates the geofence radius. We
// scale radius→zoom so small radii show a tight circle; huge radii
// zoom out to keep the whole geofence in frame.
function _zoomForRadius(radiusM) {
  if (radiusM <= 200) return 16;
  if (radiusM <= 800) return 14;
  if (radiusM <= 2500) return 12;
  return 10;
}
function staticMapUrl(lat, lng, radiusM) {
  if (!MAPBOX_TOKEN || !lat || !lng) return null;
  const zoom = _zoomForRadius(radiusM);
  const pin = `pin-l+E1301A(${lng},${lat})`;
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${pin}/${lng},${lat},${zoom},0/560x260@2x?access_token=${MAPBOX_TOKEN}`;
}

async function geocode(query) {
  if (!MAPBOX_TOKEN || !query || query.length < 3) return [];
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=us&limit=5&types=address,place,poi`;
  try {
    const r = await fetch(url);
    if (!r.ok) return [];
    const j = await r.json();
    return j.features || [];
  } catch { return []; }
}

export default function Worksites() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [wcCodes, setWcCodes] = useState([]);
  const [wcState, setWcState] = useState("");
  const debounceRef = useRef(null);

  // NCCI class codes for the worksite's state (falls back to the company's).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wc/class-codes${form.state ? `?state=${form.state}` : ""}`);
        setWcCodes(data.class_codes || []);
        setWcState(data.state || "");
      } catch { setWcCodes([]); }
    })();
  }, [form.state]);

  const pickClassCode = (e) => {
    const code = e.target.value;
    const hit = wcCodes.find((c) => c.class_code === code);
    setForm((f) => ({
      ...f,
      wc_class_code: code,
      wc_rate_per_100: hit ? Number(hit.rate_per_100 || 0) : 0,
    }));
  };

  const load = async () => { const { data } = await api.get("/worksites"); setRows(data); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setForm(emptyForm); setEditingId(null); setSuggestions([]); setOpen(true); };
  const openEdit = (r) => { setForm({ ...emptyForm, ...r }); setEditingId(r.id); setSuggestions([]); setOpen(true); };

  const upd = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: ["latitude", "longitude", "radius_meters", "client_billable_rate", "wc_rate_per_100"].includes(k) ? Number(v) : v });
  };

  // Debounced Mapbox forward-geocoder. Suggestions are shown as a
  // dropdown; picking one fills lat/lng/city/state in one click.
  const onAddressChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, address: v }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!MAPBOX_TOKEN) return;
    if (v.length < 3) { setSuggestions([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const feats = await geocode(v);
      setSuggestions(feats);
      setSearching(false);
    }, 350);
  };

  const pickSuggestion = (f) => {
    const [lng, lat] = f.center;
    // f.context is an array of { id: "place.xxx"|"region.xxx", text }
    let city = "", state = "";
    for (const c of (f.context || [])) {
      if (c.id.startsWith("place.")) city = c.text;
      if (c.id.startsWith("region.")) state = c.short_code?.replace("US-", "") || c.text;
    }
    setForm((prev) => ({
      ...prev,
      address: f.place_name.split(",")[0],
      city, state,
      latitude: +lat.toFixed(6),
      longitude: +lng.toFixed(6),
    }));
    setSuggestions([]);
  };

  const useCurrent = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm((f) => ({ ...f, latitude: +p.coords.latitude.toFixed(6), longitude: +p.coords.longitude.toFixed(6) })); toast.success("Location captured"); },
      (err) => toast.error(err.message)
    );
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) await api.put(`/worksites/${editingId}`, form);
      else await api.post("/worksites", form);
      toast.success("Worksite saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this worksite?")) return;
    await api.delete(`/worksites/${id}`);
    toast.success("Worksite deleted");
    load();
  };

  const previewUrl = staticMapUrl(form.latitude, form.longitude, form.radius_meters);

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline mb-2">§ WORKSITE MANAGEMENT</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Worksites</h1>
          <p className="text-sm text-muted-foreground mt-2">Define allowed clock-in locations. Employees clocking in outside a site's radius get flagged — clock-in is never blocked.</p>
        </div>
        <button data-testid="add-worksite-btn" onClick={openNew} className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Worksite
        </button>
      </div>

      {!MAPBOX_TOKEN && (
        <div className="mb-6 border-2 border-warn bg-warn/10 p-3 font-mono text-xs" data-testid="mapbox-token-missing">
          <b>Mapbox token missing.</b> Address autocomplete + map previews are disabled until <code>REACT_APP_MAPBOX_TOKEN</code> is set in <code>frontend/.env</code>. Manual lat/lng entry still works.
        </div>
      )}

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="worksites-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Name</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Address</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Coordinates</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Radius</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No worksites yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`worksite-row-${r.id}`}>
                <td className="px-4 py-3 border-t border-ink/20 font-semibold">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> {r.name}
                    {r.is_remote && <span className="px-1.5 py-0.5 border border-ink bg-ink text-white text-[9px] font-mono uppercase tracking-widest">Remote</span>}
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-ink/20">{r.is_remote ? <span className="font-mono text-xs text-muted-foreground">Online — no physical address</span> : <>{r.address}, {r.city} {r.state}</>}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-xs">{r.is_remote ? "—" : `${r.latitude?.toFixed(5)}, ${r.longitude?.toFixed(5)}`}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">{r.is_remote ? "n/a" : `${r.radius_meters}m`}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right space-x-2 whitespace-nowrap">
                  <button data-testid={`edit-ws-${r.id}`} onClick={() => openEdit(r)} className="border border-ink px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:bg-ink hover:text-white"><Edit className="w-3 h-3" /> Edit</button>
                  <button data-testid={`delete-ws-${r.id}`} onClick={() => remove(r.id)} className="border border-bad text-bad px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:bg-bad hover:text-white"><Trash2 className="w-3 h-3" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
          <form onSubmit={save} className="bg-white border-2 border-ink w-full max-w-2xl shadow-hard-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-ink px-6 py-4 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <div className="overline">§ WORKSITE</div>
                <h2 className="font-heading font-black text-2xl">{editingId ? "Edit Worksite" : "Add Worksite"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 border-2 border-ink hover:bg-ink hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <F label="Name" className="col-span-2"><input required data-testid="ws-name" value={form.name} onChange={upd("name")} className="ws-input" /></F>
              <div className="col-span-2">
                <label className="flex items-start gap-3 cursor-pointer p-3 border-2 border-ink bg-secondary" data-testid="ws-is-remote-row">
                  <input
                    type="checkbox"
                    data-testid="ws-is-remote"
                    checked={!!form.is_remote}
                    onChange={(e) => setForm({ ...form, is_remote: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-ink"
                  />
                  <div>
                    <div className="font-mono text-sm font-black">Remote / Online Worksite</div>
                    <div className="font-mono text-[10px] text-muted-foreground leading-relaxed mt-1">
                      Virtual location — no geofence. When enabled, address / latitude / longitude / radius are ignored and
                      hours are driven by portal login sessions (only fires if the master <b>Remote Tracking</b> toggle is ON in Company settings).
                    </div>
                  </div>
                </label>
              </div>
              {!form.is_remote && (
                <>
                <div className="col-span-2 relative">
                <div className="overline mb-2">Address {MAPBOX_TOKEN && <span className="text-[9px] text-muted-foreground normal-case">· type to search Mapbox</span>}</div>
                <div className="relative">
                  <input
                    data-testid="ws-address"
                    value={form.address}
                    onChange={onAddressChange}
                    autoComplete="off"
                    placeholder={MAPBOX_TOKEN ? "e.g. 1 World Trade Center" : "Enter address manually"}
                    className="ws-input pr-10"
                  />
                  {MAPBOX_TOKEN && <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                </div>
                {suggestions.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 border-2 border-ink bg-white shadow-hard-lg max-h-56 overflow-y-auto" data-testid="mapbox-suggestions">
                    {suggestions.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => pickSuggestion(f)}
                        data-testid={`mapbox-suggestion-${f.id}`}
                        className="w-full text-left px-3 py-2 border-b border-ink/10 hover:bg-secondary font-mono text-xs"
                      >{f.place_name}</button>
                    ))}
                  </div>
                )}
                {searching && <div className="mt-1 font-mono text-[10px] text-muted-foreground">Searching…</div>}
              </div>
              <F label="City"><input data-testid="ws-city" value={form.city} onChange={upd("city")} className="ws-input" /></F>
              <F label="State"><input data-testid="ws-state" value={form.state} onChange={upd("state")} className="ws-input" /></F>
              <F label="Latitude" className="col-span-1">
                <input required type="number" step="0.000001" data-testid="ws-lat" value={form.latitude} onChange={upd("latitude")} className="ws-input" />
              </F>
              <F label="Longitude" className="col-span-1">
                <input required type="number" step="0.000001" data-testid="ws-lng" value={form.longitude} onChange={upd("longitude")} className="ws-input" />
              </F>
              <div className="col-span-2">
                <button type="button" onClick={useCurrent} data-testid="ws-use-current-location" className="w-full border-2 border-ink py-2 font-mono uppercase text-xs tracking-widest hover:bg-secondary inline-flex items-center justify-center gap-2">
                  <Crosshair className="w-4 h-4" /> Use My Current Location
                </button>
              </div>
              <F label={`Allowed Radius (meters) · ${form.radius_meters}m`} className="col-span-2">
                <input
                  required
                  type="range"
                  min={25}
                  max={2500}
                  step={25}
                  data-testid="ws-radius-slider"
                  value={form.radius_meters}
                  onChange={upd("radius_meters")}
                  className="w-full accent-ink"
                />
                <div className="flex items-center justify-between mt-1">
                  <input required type="number" min={10} max={5000} data-testid="ws-radius" value={form.radius_meters} onChange={upd("radius_meters")} className="ws-input w-24 text-center" />
                  <span className="text-[10px] font-mono text-muted-foreground">Typical: 100–300m office · 500m+ large site</span>
                </div>
              </F>
                </>
              )}
              <F label="Client Billable Rate (agency billing)" className="col-span-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono">$</span>
                  <input
                    type="number" step="0.01" min="0"
                    data-testid="ws-billable-rate"
                    value={form.client_billable_rate}
                    onChange={upd("client_billable_rate")}
                    className="ws-input"
                  />
                  <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">/ hour</span>
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">Leave at 0 if this worksite is not billed to a client. Used by <b>Billing Concierge</b>.</div>
              </F>

              {/* Workers' Comp — Enterprise-Grade auto-premium */}
              <div className="col-span-2 mt-2 border-t-2 border-ink pt-4">
                <div className="overline mb-1 flex items-center gap-2">§ WORKERS' COMP · NCCI
                  <span className="ml-1 px-1.5 py-0.5 bg-warn text-ink text-[9px] font-black uppercase tracking-widest">Enterprise-Grade</span>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground mb-3">Every payroll run at this worksite auto-calculates <code>premium = gross × rate/100</code> per employee. Employees inherit these values when their own overrides are 0.</div>
              </div>
              <F label="NCCI Class Code">
                <select data-testid="ws-wc-class-code" value={form.wc_class_code} onChange={pickClassCode} className="ws-input">
                  <option value="">— none —</option>
                  {wcCodes.map((c) => (
                    <option key={c.class_code} value={c.class_code}>{c.class_code} · {c.description}</option>
                  ))}
                </select>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">Picking a code fills the rate below from the {wcState || "company"} schedule.</div>
              </F>
              <F label="Rate per $100 of Payroll ($)">
                <input data-testid="ws-wc-rate" type="number" step="0.01" min="0" value={form.wc_rate_per_100} onChange={upd("wc_rate_per_100")} placeholder="1.25" className="ws-input" />
                <div className="text-[10px] font-mono text-muted-foreground mt-1" data-testid="ws-wc-rate-note">Indicative NCCI loss-cost benchmark — carrier-verified rate required before binding coverage. Override it with your carrier's filed rate.</div>
              </F>

              {previewUrl && (
                <div className="col-span-2" data-testid="ws-map-preview">
                  <div className="overline mb-2">Preview</div>
                  <img
                    src={previewUrl}
                    alt="Worksite location preview"
                    className="w-full border-2 border-ink"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
            </div>
            <div className="border-t-2 border-ink px-6 py-4 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border-2 border-ink font-mono text-xs uppercase tracking-widest">Cancel</button>
              <button data-testid="ws-save" disabled={busy} type="submit" className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest">{busy ? "Saving…" : "Save Worksite"}</button>
            </div>
            <style>{`.ws-input{width:100%;padding:0.65rem 0.75rem;border:2px solid #0A0A0A;font-family:"IBM Plex Mono",monospace;font-size:0.85rem;background:#fff}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, children, className = "" }) {
  return <label className={`block ${className}`}><div className="overline mb-2">{label}</div>{children}</label>;
}
